import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Shield, AlertCircle, Loader2, Check } from "lucide-react";
import { EMERALD_DARK } from "../../../constants/colors";
import "../LoginScreen.css";
import { totpSchema, type TotpInput } from "../../../lib/validation/schemas/auth.schema";

interface TotpVerifyStepProps {
  loading: boolean;
  serverError: string;
  onSubmit: (code: string) => void;
  onBack: () => void;
}

export default function TotpVerifyStep({
  loading,
  serverError,
  onSubmit,
  onBack,
}: TotpVerifyStepProps) {
  const { t } = useTranslation(["auth", "common"]);
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<TotpInput>({
    resolver: zodResolver(totpSchema),
    defaultValues: { totpCode: "" },
    mode: "onChange",
  });

  const errorMessage = errors.totpCode?.message ?? serverError;

  return (
    <div className="ls-step-container">
      <div className="ls-step-back-wrapper">
        <button
          type="button"
          onClick={onBack}
          className="ls-back-button"
        >
          <ArrowLeft size={16} /> {t("common:actions.back")}
        </button>
      </div>

      <div className="ls-step-icon-circle">
        <Shield size={28} color={EMERALD_DARK} />
      </div>

      <h2 className="ls-step-title">
        {t("totp.title")}
      </h2>
      <p className="ls-step-text ls-step-text--last">
        {t("totp.subtitle")}
      </p>

      {errorMessage && (
        <div className="ls-error-banner ls-error-banner--step">
          <AlertCircle size={16} className="ls-error-icon" />
          <span>{errorMessage}</span>
        </div>
      )}

      <form
        onSubmit={handleSubmit((data) => onSubmit(data.totpCode))}
        className="ls-step-form"
      >
        <div className="ls-input-group">
          <label className="ls-input-label">{t("totp.codeLabel")}</label>
          <div className="auth-input-container ls-input-container ls-input-container--centered">
            <Controller
              control={control}
              name="totpCode"
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  inputMode="numeric"
                  placeholder="000000"
                  onChange={(e) => field.onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="ls-form-input ls-otp-input"
                  disabled={loading}
                  maxLength={6}
                  autoFocus
                  autoComplete="one-time-code"
                />
              )}
            />
          </div>
        </div>

        <button
          type="submit"
          className={`ls-btn-primary${loading || !isValid ? " ls-btn-primary--disabled" : ""}`}
          disabled={loading || !isValid}
        >
          {loading ? (
            <Loader2 size={18} className="ls-spinner" />
          ) : (
            <Check size={18} />
          )}
          <span>{loading ? t("common:actions.verifying") : t("totp.verify")}</span>
        </button>
      </form>
    </div>
  );
}
